import AlertMessage from './AlertMessage';
import { useState, useEffect } from 'react';
import axios from 'axios';

// Helper to read any cookie by name (needed for Django CSRF)
const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
};

// Create axios instance with base URL
const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
    },
});

const Newsletter = () => {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [alert, setAlert] = useState({
        show: false,
        type: 'success',
        title: '',
        message: '',
        duration: 5000
    });

    // Check auth status on mount
    useEffect(() => {
        const token = localStorage.getItem('authToken');
        setIsLoggedIn(!!token);
    }, []);

    const showAlert = (type, title, message, duration = 5000) => {
        setAlert({ show: true, type, title, message, duration });
    };

    const hideAlert = () => {
        setAlert(prev => ({ ...prev, show: false }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Guest-only: validate the email they typed
        if (!isLoggedIn && (!email || !/\S+@\S+\.\S+/.test(email))) {
            showAlert('error', 'Invalid Email', 'Please enter a valid email address.');
            return;
        }

        setLoading(true);
        setAlert(prev => ({ ...prev, show: false }));

        try {
            const token = localStorage.getItem('authToken');
            const csrfToken = getCookie('csrftoken');

            const headers = { 'Content-Type': 'application/json' };
            if (token) headers['Authorization'] = `Bearer ${token}`;
            if (csrfToken) headers['X-CSRFToken'] = csrfToken;

            // For authenticated users, read the email stored at login.
            // The backend still requires an email field even with a valid JWT.
            let payload = { email };
            if (isLoggedIn) {
                const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
                const userEmail = storedUser.email || '';
                payload = { email: userEmail };
            }

            const response = await api.post('/newsletter/subscribe/', payload, { headers });

            if (response.data.pending_verification) {
                // Guest — awaiting email confirmation
                showAlert(
                    'info',
                    'Check Your Email',
                    `A confirmation link has been sent to ${response.data.email}. Please click it to activate your subscription.`,
                    8000
                );
                setEmail('');
            } else {
                // Authenticated — subscribed immediately
                showAlert(
                    'success',
                    'Subscribed Successfully!',
                    response.data.message || 'Thank you for subscribing! You\'ll receive our latest energy insights.',
                    5000
                );
                setEmail('');
            }

        } catch (error) {

            let errorMessage = 'An unexpected error occurred. Please try again.';
            let errorTitle = 'Subscription Failed';

            if (error.response) {
                const errorData = error.response.data;
                errorMessage = errorData.error || errorData.message || errorData.email?.[0] || 'Failed to subscribe. Please try again.';
                errorTitle = error.response.status === 400 ? 'Already Subscribed' : 'Subscription Error';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection and try again.';
                errorTitle = 'Network Error';
            }

            showAlert('error', errorTitle, errorMessage, 7000);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {/* Alert Message Component */}
            {alert.show && (
                <AlertMessage
                    type={alert.type}
                    title={alert.title}
                    message={alert.message}
                    duration={alert.duration}
                    onDismiss={hideAlert}
                />
            )}

            <section id="call-to-action" className="call-to-action section light-background py-16 bg-gray-50 w-full">
                <div className="w-full max-w-full">
                    <div className="container mx-auto px-4">
                        <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
                            {/* Text Content */}
                            <div className="w-full lg:w-1/2">
                                <h3 className="text-3xl font-bold text-gray-800 mb-4">
                                    Stay Updated With The Latest  From Alternative Energy
                                </h3>
                                <p className="text-gray-600 text-lg">
                                    Subscribe to our newsletter for exclusive updates on solar energy innovations,
                                    electrical system tips, special offers, and industry insights to help you make
                                    informed energy decisions.
                                </p>
                            </div>

                            {/* Form */}
                            <div className="w-full lg:w-1/2">
                                <form
                                    onSubmit={handleSubmit}
                                    className="form-subscribe php-email-form"
                                >
                                    <div className="flex w-full">
                                        {isLoggedIn ? (
                                            /* Authenticated: just show the button */
                                            <button
                                                type="submit"
                                                className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-3 rounded-lg transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                                disabled={loading}
                                            >
                                                {loading ? (
                                                    <span className="flex items-center justify-center">
                                                        <svg className="animate-spin h-5 w-5 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                        Subscribing...
                                                    </span>
                                                ) : 'Subscribe Now'}
                                            </button>
                                        ) : (
                                            /* Guest: show email input + button */
                                            <>
                                                <input
                                                    type="email"
                                                    name="email"
                                                    value={email}
                                                    onChange={(e) => setEmail(e.target.value)}
                                                    className="flex-grow min-w-0 px-4 py-3 border-2 border-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent rounded-l-lg placeholder-gray-500 placeholder-opacity-100"
                                                    placeholder="Your email address"
                                                    required
                                                    disabled={loading}
                                                    style={{
                                                        color: '#374151',
                                                        backgroundColor: 'white'
                                                    }}
                                                />
                                                <button
                                                    type="submit"
                                                    className="bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-3 rounded-r-lg transition-colors duration-300 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                                                    disabled={loading}
                                                >
                                                    {loading ? (
                                                        <span className="flex items-center justify-center">
                                                            <svg className="animate-spin h-5 w-5 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                            </svg>
                                                            Subscribing...
                                                        </span>
                                                    ) : 'Subscribe Now'}
                                                </button>
                                            </>
                                        )}
                                    </div>

                                    {/* Privacy Notice */}
                                    <div className="mt-4 text-sm text-gray-500">
                                        {isLoggedIn ? (
                                            <p>
                                                You'll be subscribed using your account email. We respect your privacy and will never share your information.
                                            </p>
                                        ) : (
                                            <p>
                                                By subscribing, you agree to receive updates about our solar and electrical services.
                                                We'll send a confirmation email to verify your address before you're added to the list.
                                            </p>
                                        )}
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default Newsletter;